import { useState } from 'react'
import type { MetaAppSettings, MetaAppUpdateRequest } from '@rp/contracts'
import { isApiError } from '../../api/client'
import { API_COPY } from '../../api/copy'
import { Button, Card, Dialog, Spinner } from '../../design'
import { META_APP_COPY } from './copy'
import { MetaAppDetails } from './MetaAppDetails'
import { MetaAppForm } from './MetaAppForm'
import { useMetaApp, useSaveMetaApp } from './metaAppQueries'

type Mode = 'view' | 'secret' | 'app'

const HEADING_ID = 'meta-app-heading'

type MetaAppSectionBodyProps = {
  data: MetaAppSettings | null
  mode: Mode
  setMode: (mode: Mode) => void
  pending: boolean
  formError: string | null
  onSubmit: (value: { appId: string; appSecret: string }) => void
}

const MetaAppSectionForm = ({ data, mode, setMode, pending, formError, onSubmit }: MetaAppSectionBodyProps) => {
  if (data === null) {
    return (
      <>
        <p className="settings-hint measure">{META_APP_COPY.missing}</p>
        <MetaAppForm initialAppId="" pending={pending} error={formError} onSubmit={onSubmit} />
      </>
    )
  }
  if (mode === 'view') {
    return <MetaAppDetails settings={data} onReplaceSecret={() => setMode('secret')} onReplaceApp={() => setMode('app')} />
  }
  return (
    <MetaAppForm
      initialAppId={mode === 'secret' ? data.appId : ''}
      pending={pending}
      error={formError}
      onSubmit={onSubmit}
      onCancel={() => setMode('view')}
    />
  )
}

export const MetaAppSection = () => {
  const metaApp = useMetaApp()
  const save = useSaveMetaApp()
  const [mode, setMode] = useState<Mode>('view')
  const [pendingRequest, setPendingRequest] = useState<MetaAppUpdateRequest | null>(null)

  const submit = (value: { appId: string; appSecret: string }) => {
    const body: MetaAppUpdateRequest = { ...value, confirmDisconnect: false }
    save.mutate(body, {
      onSuccess: () => {
        setMode('view')
        setPendingRequest(null)
      },
      onError: (error) => {
        setPendingRequest(isApiError(error) && error.code === 'META_APP_CHANGE_DISCONNECTS' ? body : null)
      },
    })
  }

  const confirmChange = () => {
    if (pendingRequest === null) return
    save.mutate(
      { ...pendingRequest, confirmDisconnect: true },
      {
        onSuccess: () => {
          setMode('view')
          setPendingRequest(null)
        },
        onError: () => setPendingRequest(null),
      },
    )
  }

  const cancelDialog = () => {
    setPendingRequest(null)
    save.reset()
  }

  if (metaApp.isPending) {
    return (
      <Card as="section" className="settings-card">
        <Spinner label={META_APP_COPY.loading} />
      </Card>
    )
  }

  if (metaApp.isError) {
    return (
      <Card as="section" className="settings-card">
        <p className="settings-error text-tone tone-danger" role="alert">
          {isApiError(metaApp.error) ? metaApp.error.message : API_COPY.malformed}
        </p>
      </Card>
    )
  }

  const formError = pendingRequest !== null ? null : isApiError(save.error) ? save.error.message : null
  const dialogMessage = isApiError(save.error) ? save.error.message : ''

  return (
    <Card as="section" className="settings-card">
      <section className="meta-app-section" aria-labelledby={HEADING_ID}>
        <h2 id={HEADING_ID}>{META_APP_COPY.title}</h2>
        <MetaAppSectionForm
          data={metaApp.data}
          mode={mode}
          setMode={setMode}
          pending={save.isPending}
          formError={formError}
          onSubmit={submit}
        />
        <Dialog
          open={pendingRequest !== null}
          title={META_APP_COPY.confirmTitle}
          onClose={cancelDialog}
          footer={
            <>
              <Button onClick={cancelDialog}>{META_APP_COPY.cancel}</Button>
              <Button variant="primary" pending={save.isPending} onClick={confirmChange}>
                {META_APP_COPY.confirm}
              </Button>
            </>
          }
        >
          <p className="measure">{dialogMessage}</p>
        </Dialog>
      </section>
    </Card>
  )
}
